<template>
  <!-- Proxy login: username + password, or an IP whitelist (login window on My Proxies and the purchase window) -->
  <div class="plf">
    <div class="plf-seg" role="tablist">
      <button type="button" role="tab" :aria-selected="model.mode === 'login'" :class="{ on: model.mode === 'login' }" @click="model = { ...model, mode: 'login' }">
        <v-icon icon="mdi-key-variant" size="15"></v-icon> {{ t('login.tabLogin') }}</button>
      <button type="button" role="tab" :aria-selected="model.mode === 'ips'" :class="{ on: model.mode === 'ips' }" @click="model = { ...model, mode: 'ips' }">
        <v-icon icon="mdi-web" size="15"></v-icon> {{ t('login.tabIps') }}</button>
    </div>

    <template v-if="model.mode === 'login'">
      <div class="plf-grid">
        <label class="plf-field">
          <span class="plf-lbl">{{ t('login.username') }}</span>
          <input :value="model.user" @input="model = { ...model, user: $event.target.value.trim() }" class="plf-in" autocomplete="off" spellcheck="false" maxlength="32" />
          <small :class="hintClass(model.user)"><v-icon v-if="nameOk(model.user, minLength)" icon="mdi-check" size="12"></v-icon>{{ hintText }}</small>
        </label>
        <label class="plf-field">
          <span class="plf-lbl">{{ t('login.password') }}</span>
          <span class="plf-wrap">
            <input :value="model.pass" @input="model = { ...model, pass: $event.target.value.trim() }" :type="show ? 'text' : 'password'" class="plf-in" autocomplete="new-password" spellcheck="false" maxlength="32" />
            <button type="button" class="plf-eye" :aria-label="show ? t('login.hide') : t('login.show')" @click="show = !show"><v-icon :icon="show ? 'mdi-eye-off-outline' : 'mdi-eye-outline'" size="17"></v-icon></button>
          </span>
          <small :class="hintClass(model.pass)"><v-icon v-if="nameOk(model.pass, minLength)" icon="mdi-check" size="12"></v-icon>{{ hintText }}</small>
        </label>
      </div>
      <div class="plf-links">
        <button type="button" @click="model = { ...model, ...randomLogin() }"><v-icon icon="mdi-refresh" size="14"></v-icon> {{ t('login.random') }}</button>
        <button type="button" @click="copyLine"><v-icon icon="mdi-content-copy" size="14"></v-icon> {{ t('login.copy') }}</button>
      </div>
      <div>
        <span class="plf-lbl">{{ host && port ? t('login.willBe') : t('login.yourLogin') }}</span>
        <div class="plf-prev" translate="no">{{ line }}</div>
      </div>
    </template>

    <template v-else>
      <label class="plf-field">
        <span class="plf-lbl">{{ t('login.ipsLabel') }}</span>
        <textarea :value="model.ips" @input="model = { ...model, ips: $event.target.value }" rows="3" class="plf-in plf-area" spellcheck="false" placeholder="1.1.1.1&#10;2.2.2.2"></textarea>
        <small v-if="badIps.length" class="bad">{{ t('login.badIps', { ips: badIps.join(', ') }) }}</small>
        <small v-else-if="ipList.length" class="ok"><v-icon icon="mdi-check" size="12"></v-icon>{{ t('login.ipsOk', { n: ipList.length }) }}</small>
        <small v-else>{{ t('login.ipsHint') }}</small>
      </label>
      <div class="plf-links">
        <button type="button" :disabled="findingIp" @click="addMyIp"><v-icon icon="mdi-plus" size="14"></v-icon> {{ t('login.addMyIp') }}</button>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, defineModel, defineProps } from 'vue';
import copy from 'clipboard-copy';
import { t } from '@/i18n';
import { fetchWrapper } from '@/helpers';
import { useAlertStore } from '@/stores';
import { nameOk, ipOk, splitIps, randomLogin } from '@/helpers/proxyLogin';

const props = defineProps({ minLength: { type: Number, default: 1 }, host: String, port: [String, Number] });
const model = defineModel({ type: Object, required: true });   // { mode: 'login' | 'ips', user, pass, ips }
const show = ref(false);
const findingIp = ref(false);

const hintText = computed(() => props.minLength > 1 ? t('login.ruleMin', { n: props.minLength }) : t('login.rule'));
const hintClass = (v) => (!v ? '' : nameOk(v, props.minLength) ? 'ok' : 'bad');
const line = computed(() => {
  const login = `${model.value.user || '…'}:${model.value.pass || '…'}`;
  return props.host && props.port ? `${props.host}:${props.port}:${login}` : login;
});
const ipList = computed(() => splitIps(model.value.ips));
const badIps = computed(() => ipList.value.filter((ip) => !ipOk(ip)));

const copyLine = () => { copy(line.value); useAlertStore().success(t('common.copied')); };
const addMyIp = async () => {
  findingIp.value = true;
  try {
    const r = await fetchWrapper.get(`${process.env.VUE_APP_VUE_API_URL}/proxy/my-ip`);
    const ip = String((r && r.ip) || '');
    if (!ipOk(ip)) useAlertStore().error(t('login.myIpNotV4', { ip }));
    else if (!ipList.value.includes(ip)) model.value = { ...model.value, ips: [...ipList.value, ip].join('\n') };
  } catch (e) { useAlertStore().error(e); }
  findingIp.value = false;
};
</script>

<style scoped>
.plf { display: flex; flex-direction: column; gap: 12px; }
.plf-seg { display: grid; grid-template-columns: 1fr 1fr; border: 1px solid var(--ui-line); border-radius: 8px; overflow: hidden; }
.plf-seg button { display: inline-flex; align-items: center; justify-content: center; gap: 6px; padding: 7px 10px; font-size: 13px; color: inherit; background: transparent; }
.plf-seg button.on { background: var(--ui-pri); color: #ffffff; }
.plf-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.plf-field { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.plf-lbl { display: block; font-size: 12px; color: var(--ui-dim); margin-bottom: 2px; }
.plf-in { width: 100%; height: 36px; padding: 0 10px; border: 1px solid var(--ui-line-strong); border-radius: 8px; background: transparent; color: inherit; font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 13px; }
.plf-in:focus { outline: none; border-color: var(--ui-pri); }
.plf-area { height: auto; padding: 8px 10px; resize: vertical; }
.plf-wrap { position: relative; display: block; }
.plf-wrap .plf-in { padding-right: 34px; }
.plf-eye { position: absolute; right: 6px; top: 50%; transform: translateY(-50%); color: var(--ui-dim); background: transparent; }
.plf-field small { font-size: 11px; color: var(--ui-dim); display: inline-flex; align-items: center; gap: 2px; }
.plf-field small.ok { color: #22c55e; }
.plf-field small.bad { color: #ef4444; }
.plf-links { display: flex; justify-content: space-between; gap: 10px; }
.plf-links button { display: inline-flex; align-items: center; gap: 4px; font-size: 12px; color: var(--ui-pri-ink, #8f8bf5); background: transparent; }
.plf-links button:disabled { opacity: 0.5; }
.plf-prev { border: 1px dashed var(--ui-line-strong); border-radius: 8px; padding: 7px 10px; font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 12px; overflow-wrap: anywhere; }
</style>
